import { useEffect, useId, type ReactNode } from 'react';
import { X } from '../../../icons';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import './SidePanel.css';

/**
 * SidePanel — 오른쪽 패널 (2026-10-18 · web 레인 W10-51 · 렌즈 Karri · 계획 8.2)
 *
 * 머리 오른쪽 주 단추(«+ 새 ○○»)로 여는 입력 칸이다. 51 인사발령 · 56 포상 · 징계가 쓴다.
 * 쪽 지역 `UserDrawer`(사용자 관리)와 같은 꼴을 공용으로 세운 것이다(그 화면은 이 판에서 옮기지 않는다).
 *   ★ 부모가 열 때만 그린다(`{open && <SidePanel …/>}`). 닫으면 사라지고 초점은 **연 단추로 돌아간다**(useFocusTrap).
 *   ★ Esc · 덮개 누르기 · 닫기 단추로 닫는다. 보내는 중(`busy`)에는 Esc · 덮개가 닫지 않는다.
 *   ★ 초점은 패널 안을 돈다(Tab · Shift+Tab). 열면 **몸의 첫 칸**에 선다(닫기 단추가 아니라).
 */
export interface SidePanelProps {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  busy?: boolean;
  className?: string;
}

const FIRST_FIELD = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

export function SidePanel({ title, description, onClose, footer, children, busy = false, className }: SidePanelProps) {
  const trapRef = useFocusTrap<HTMLElement>(true);
  useEscapeClose(onClose, busy);
  const titleId = useId();

  // 열면 몸의 첫 칸에 — useFocusTrap 이 먼저 «연 단추»를 기억한 뒤(같은 커밋 · 먼저 선언된 효과) 옮긴다.
  useEffect(() => {
    const body = trapRef.current?.querySelector<HTMLElement>('.wc-sp__body');
    body?.querySelector<HTMLElement>(FIRST_FIELD)?.focus();
  }, [trapRef]);

  return (
    <>
      <div className="wc-sp-scrim" aria-hidden onMouseDown={() => { if (!busy) onClose(); }} />
      <aside
        ref={trapRef}
        className={['wc-sp', className ?? ''].filter(Boolean).join(' ')}
        role="dialog" aria-modal="true" aria-labelledby={titleId}
      >
        <header className="wc-sp__head">
          <div className="wc-sp__t">
            <h2 id={titleId}>{title}</h2>
            {description != null && <p>{description}</p>}
          </div>
          <button type="button" className="wc-sp__x" aria-label="닫기" onClick={onClose}>
            <X aria-hidden />
          </button>
        </header>
        <div className="wc-sp__body">{children}</div>
        {footer != null && <footer className="wc-sp__foot">{footer}</footer>}
      </aside>
    </>
  );
}
